'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { alpha, LinearProgress, styled } from '@mui/material';
import { useIsFetching } from '@tanstack/react-query';

const StyledProgress = styled(LinearProgress, {
  shouldForwardProp: (prop) => prop !== 'visible',
})<{ visible?: boolean }>(({ theme, visible }) => ({
  height: 3,
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 10100,
  backgroundColor: 'transparent',
  opacity: visible ? 1 : 0,
  transition: 'opacity 0.4s ease-in-out',
  pointerEvents: 'none',
  '& .MuiLinearProgress-bar': {
    transition: 'transform 0.4s cubic-bezier(0.1, 0.7, 0.1, 1)',
    backgroundColor: theme.palette.primary.main,
    boxShadow: `0 0 12px ${alpha(theme.palette.primary.main, 0.6)}`,
    borderRadius: '0 2px 2px 0',
  },
}));

export const AppLoadingBar = () => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isFetchingCount = useIsFetching({
    predicate: (query: any) => {
      // Ignore background refetches where data is already cached
      if (query.state.data !== undefined) return false;

      // Ignore polling queries
      if (query.options.refetchInterval) return false;

      return true;
    },
  });

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const finishTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isRouteChanging = useRef(false);

  const startLoading = useCallback(() => {
    if (finishTimerRef.current) {
      clearTimeout(finishTimerRef.current);
      finishTimerRef.current = null;
    }

    setLoading(true);
    setProgress((prev) => {
      // If we are already mid-progress, don't jump back
      if (prev > 0 && prev < 100) return prev;
      return 2; // Start small for smoothness
    });

    if (!intervalRef.current) {
      intervalRef.current = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            return 90;
          }
          // Dynamic increment: faster at start, slower as it nears 90%
          const remaining = 92 - prev;
          const increment = Math.max(0.05, remaining / 60);
          return prev + increment;
        });
      }, 50); // High frequency for ultra-smoothness
    }
  }, []);

  const finishLoading = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setProgress(100);

    if (finishTimerRef.current) clearTimeout(finishTimerRef.current);
    finishTimerRef.current = setTimeout(() => {
      setLoading(false);
      // Wait for the opacity fade-out before resetting progress
      setTimeout(() => {
        setProgress(0);
      }, 400);
      finishTimerRef.current = null;
    }, 400);
  }, []);

  // 1. Handle Route Changes
  useEffect(() => {
    isRouteChanging.current = true;
    const startTimer = setTimeout(() => {
      startLoading();
    }, 0);

    // Most routes settle within 600ms
    const timer = setTimeout(() => {
      isRouteChanging.current = false;
      if (isFetchingCount === 0) {
        finishLoading();
      }
    }, 600);

    return () => {
      clearTimeout(startTimer);
      clearTimeout(timer);
    };
  }, [pathname, searchParams, startLoading, finishLoading]); // NO isFetchingCount here to avoid back-and-forth

  // 2. Handle API Fetching
  useEffect(() => {
    let startTimer: NodeJS.Timeout | null = null;
    let timer: NodeJS.Timeout | null = null;

    if (isFetchingCount > 0) {
      startTimer = setTimeout(() => {
        startLoading();
      }, 0);
    } else if (!isRouteChanging.current) {
      // Only finish if no route change is pending
      timer = setTimeout(() => {
        if (isFetchingCount === 0 && !isRouteChanging.current) {
          finishLoading();
        }
      }, 400);
    }

    return () => {
      if (startTimer) clearTimeout(startTimer);
      if (timer) clearTimeout(timer);
    };
  }, [isFetchingCount, startLoading, finishLoading]);

  // 3. Cleanup
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (finishTimerRef.current) clearTimeout(finishTimerRef.current);
    };
  }, []);

  return (
    <StyledProgress
      id="app-loading-bar"
      variant="determinate"
      value={progress}
      visible={loading}
      suppressHydrationWarning
    />
  );
};
